import React from 'react';

import { get as getConfig } from '@lumapps/constants';
import { customerIdSelector } from '@lumapps/customer/ducks/selectors';
import { AvatarProps } from '@lumapps/lumx/react';
import { resizeImageToLumxSize } from '@lumapps/medias/utils/resizeImage';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { INACTIVE_USER_AVATAR } from '../constants';
import { blobAvatars } from '../ducks/selectors';
import { MinimumViableUser, UseUserAvatarOptions } from '../types';
import { getUserFullName } from '../utils/getUserFullName';
import { getUserProfilePictureUrl } from '../utils/getUserProfilePicture';
import { isUserInactive } from '../utils/isUserInactive';

export type UseUserAvatarResult = AvatarProps & {
    /** retrieve a user's profile picture */
    getUserProfilePicture: () => string;
};

const { userLang } = getConfig();

/**
 * Hook in order to retrieve the necessary props for displaying a user's avatar.
 * @param {MinimumViableUser} user
 * @param {UseUserAvatarOptions} options
 * @returns {UseUserAvatarResult}
 */
export const useUserAvatar = (user?: MinimumViableUser, options: UseUserAvatarOptions = {}): UseUserAvatarResult => {
    const customerId = useSelector(customerIdSelector);
    const currentBlobAvatars = useSelector(blobAvatars);
    const { translateKey } = useTranslate();
    const { customUserAvatar, resizeAvatarTo, useAvatarFromApiProfile = false } = options;

    return React.useMemo(() => {
        const image =
            // If profilePictureUrl is set, use it
            user?.profilePictureUrl ||
            // If profilePicture inside apiProfile is set, use it
            user?.apiProfile?.profilePicture ||
            // If apiProfile.thumbnail has photoData, use it
            (user?.apiProfile && user?.apiProfile.thumbnail && user?.apiProfile.thumbnail.photoData
                ? `data:image/jpeg;base64,${user?.apiProfile.thumbnail.photoData}`
                : undefined);

        const profilePictureUrl =
            useAvatarFromApiProfile && image
                ? image
                : // eslint-disable-next-line deprecation/deprecation
                  getUserProfilePictureUrl(
                      { id: user?.id, customer: customerId },
                      currentBlobAvatars && user?.id ? currentBlobAvatars[user.id] : undefined,
                  );

        // If the user is inactive, use the INACTIVE_USER_AVATAR
        const calculatedProfilePicture = isUserInactive(user)
            ? INACTIVE_USER_AVATAR
            : customUserAvatar || profilePictureUrl;

        // eslint-disable-next-line deprecation/deprecation
        const fullName = getUserFullName(user, userLang);

        return {
            alt: fullName && fullName !== '' ? fullName : translateKey(GLOBAL.UNKNOWN_USER),
            image: resizeAvatarTo
                ? resizeImageToLumxSize(calculatedProfilePicture, resizeAvatarTo)
                : calculatedProfilePicture,
            getUserProfilePicture: () => calculatedProfilePicture,
        };
    }, [customUserAvatar, useAvatarFromApiProfile, user, customerId, currentBlobAvatars, translateKey, resizeAvatarTo]);
};
